import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, Send as SendIcon } from '@mui/icons-material';
import {
    Box,
    Collapse,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { mockMessageService } from '../services/MockMessageService';
import type { IMessage } from '../types/IMessage';

export function SentTab() {
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    useEffect(() => {
        loadMessages();
    }, []);

    const loadMessages = async () => {
        try {
            const sentMessages = await mockMessageService.getSentMessages();
            setMessages(sentMessages);
        } catch (error) {
            console.error('Error loading sent messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleExpand = (messageId: string) => {
        setExpandedMessage(expandedMessage === messageId ? null : messageId);
    };

    const formatTimestamp = (timestamp: Date) => {
        const now = new Date();
        const diff = now.getTime() - new Date(timestamp).getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days} ${langStrings.messages.daysAgo}`;
        } else if (hours > 0) {
            return `${hours} ${langStrings.messages.hoursAgo}`;
        } else {
            return langStrings.messages.justNow;
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {langStrings.messages.loadingSentMessages}
                </Typography>
            </Box>
        );
    }

    if (messages.length === 0) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {langStrings.messages.noSentMessages}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Paper elevation={1}>
                <List sx={{ p: 0 }}>
                    {messages.map((message, index) => {
                        const isExpanded = expandedMessage === message.id;
                        return (
                            <React.Fragment key={message.id}>
                                <ListItem
                                    sx={{
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        cursor: 'pointer',
                                        py: { xs: 1.5, sm: 2 },
                                        px: { xs: 1, sm: 2 },
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            width: '100%',
                                            gap: { xs: 1, sm: 2 },
                                        }}
                                        onClick={() => handleToggleExpand(message.id)}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                                mt: 0.5,
                                            }}
                                        >
                                            <SendIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                    mb: 0.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: { xs: 2, sm: 1 },
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {message.subject}
                                            </Typography>

                                            {!isExpanded && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                        mb: 0.5,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: { xs: 2, sm: 1 },
                                                        WebkitBoxOrient: 'vertical',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {message.content}
                                                </Typography>
                                            )}

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                                }}
                                            >
                                                An:{' '}
                                                {message.to
                                                    .map((recipient) => `${recipient.firstName} ${recipient.lastName}`)
                                                    .join(', ')}{' '}
                                                • {formatTimestamp(message.timestamp)}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                                mt: 0.5,
                                            }}
                                        >
                                            {isExpanded ? (
                                                <ExpandLessIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                            ) : (
                                                <ExpandMoreIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                            )}
                                        </Box>
                                    </Box>

                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box
                                            sx={{
                                                pl: { xs: 1, sm: 7 },
                                                pr: { xs: 1, sm: 2 },
                                                pb: 2,
                                                pt: 1,
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Divider sx={{ mb: 2 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    lineHeight: 1.5,
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                }}
                                            >
                                                {message.content}
                                            </Typography>
                                        </Box>
                                    </Collapse>
                                </ListItem>
                                {index < messages.length - 1 && <Divider />}
                            </React.Fragment>
                        );
                    })}
                </List>
            </Paper>
        </Box>
    );
}
